import { useEffect, useMemo } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  Download,
  FileBarChart,
  Search,
  Users,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { MetricTile } from '@/components/ui/MetricTile';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Sparkline } from '@/components/ui/Sparkline';
import { Table, type Column } from '@/components/ui/Table';
import { useApiQuery } from '@/hooks/useApiQuery';
import { downloadCsv } from '@/lib/download';
import { getInitials } from '@/lib/utils';
import type { AttendanceStudentTotals } from '@/types/attendance';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { teacherApi } from '../../api/teacher.api';
import {
  DEFAULTER_THRESHOLD,
  REPORT_TYPE_OPTIONS,
  SERIOUS_DEFAULTER_THRESHOLD,
  type AttendanceReportType,
} from '../../constants/attendanceReport';
import type { AllocatedClass } from '../../types/teacher';
import { formatInstituteDate } from '../../utils';
import { getAvatarColor } from '../../utils/avatarColor';

/**
 * One section's attendance over a period.
 *
 * Every figure is counted from registers that were actually taken. Nothing here
 * is derived from anything else: the rates, the trend and each child's row are
 * the same marks a teacher entered, added up.
 *
 * **A day nobody marked is not a day everybody was absent.** It is left out of
 * the totals and leaves a gap in the trend, which is why "Days Recorded" sits
 * beside "Total Working Days" — a 100% month over two registers is a different
 * claim from a 100% month over twenty.
 *
 * Read from `GET /institute/attendance/summary`, which answers for the section
 * the page header has chosen and for the window it is showing. The server does
 * the counting — the totals, the daily trend and each child's rolling rate all
 * come back already added up, because a client that re-derived them could
 * disagree with the parent's copy of the same figures.
 *
 * The report type below is the one thing applied here: all three are lenses on
 * the same answer, so narrowing in the client is one request rather than three.
 */

interface TeacherAttendanceReportsViewProps {
  currentClass: AllocatedClass;
  /** The period the page header is showing. YYYY-MM-DD, both inclusive. */
  from: string;
  to: string;
  onRegisterExport?: (fn: (() => void) | null, available: boolean) => void;
  onFilterStateChange?: (isFiltered: boolean) => void;
}

/** One row of the table: a child, plus where they sit in the list. */
interface ReportRow extends AttendanceStudentTotals {
  serial: number;
}

/** A rate as the screen prints it — never a nought that was never recorded. */
function formatPercent(percent: number | null): string {
  return percent === null ? '—' : `${percent}%`;
}

/** How a rate reads as a colour. Only ever alongside the number itself. */
function rateTone(percent: number | null): string {
  if (percent === null) return 'bg-slate-400';
  if (percent < DEFAULTER_THRESHOLD) return 'bg-rose-500';
  if (percent < 90) return 'bg-amber-500';

  return 'bg-emerald-500';
}

/**
 * Which report and search, in the address under `report.`, so a refresh keeps
 * them. The dates are the attendance screen's own and come in as props.
 */
type ReportFilters = { type: AttendanceReportType; search: string };
const DEFAULT_REPORT_FILTERS: ReportFilters = { type: 'CLASS_SUMMARY', search: '' };
const REPORT_FILTER_OPTIONS = {
  prefix: 'report',
  allowed: { type: REPORT_TYPE_OPTIONS.map((option) => option.value) },
};

export function TeacherAttendanceReportsView({
  currentClass,
  from,
  to,
  onRegisterExport,
  onFilterStateChange,
}: TeacherAttendanceReportsViewProps) {
  const [filters, setFilters] = useUrlFilters(DEFAULT_REPORT_FILTERS, REPORT_FILTER_OPTIONS);
  const { type: reportType, search: searchQuery } = filters;
  const setReportType = (type: AttendanceReportType) => setFilters({ type });
  const setSearchQuery = (search: string) => setFilters({ search });

  const sectionUuid = currentClass.classSectionUuid;

  const isInternalFiltered = reportType !== 'CLASS_SUMMARY' || searchQuery.trim().length > 0;
  useEffect(() => {
    onFilterStateChange?.(isInternalFiltered);
    return () => {
      onFilterStateChange?.(false);
    };
  }, [isInternalFiltered, onFilterStateChange]);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => teacherApi.attendanceSummary(sectionUuid, from, to),
    [sectionUuid, from, to],
  );

  /**
   * Everybody on the roster, whatever the table is narrowed to.
   *
   * The headline tiles count the class, so this stays the server's whole answer
   * and only `rows` below is filtered — a defaulters report showing "3 students
   * on the roster" would be reading the lens as if it were the class.
   */
  const students = useMemo(() => data?.students ?? [], [data]);

  const rows = useMemo<ReportRow[]>(() => {
    const byType =
      reportType === 'DEFAULTERS'
        ? students.filter(
            (student) => student.percent !== null && student.percent < DEFAULTER_THRESHOLD,
          )
        : reportType === 'PERFECT'
          ? students.filter((student) => student.percent === 100)
          : students;

    const needle = searchQuery.trim().toLowerCase();
    const matching = needle
      ? byType.filter(
          (student) =>
            student.fullName.toLowerCase().includes(needle) ||
            (student.rollNumber ?? '').toLowerCase().includes(needle) ||
            student.admissionNumber.toLowerCase().includes(needle),
        )
      : byType;

    return matching.map((student, index) => ({ ...student, serial: index + 1 }));
  }, [students, reportType, searchQuery]);

  const totals = data?.summary;

  /**
   * The class's students, each counted once, by their attendance in the period.
   *
   * Not present / absent / late: over a period those count marks — one per
   * child per recorded day — and a ring of marks beside "Total Students" reads
   * as more children present than the class has. Banded on the same 75% line
   * the Defaulters lens uses, so the two never disagree about who is short.
   */
  const slices = useMemo<DonutSlice[]>(() => {
    const inBand = (test: (percent: number) => boolean) =>
      students.filter((student) => student.percent !== null && test(student.percent)).length;
    const unmarked = students.filter((student) => student.percent === null).length;

    return [
      {
        key: 'safe',
        label: `${DEFAULTER_THRESHOLD}% and above`,
        value: inBand((percent) => percent >= DEFAULTER_THRESHOLD),
        stroke: 'stroke-emerald-500',
        dot: 'bg-emerald-500',
      },
      {
        key: 'short',
        label: `${SERIOUS_DEFAULTER_THRESHOLD}–${DEFAULTER_THRESHOLD - 1}%`,
        value: inBand(
          (percent) => percent >= SERIOUS_DEFAULTER_THRESHOLD && percent < DEFAULTER_THRESHOLD,
        ),
        stroke: 'stroke-amber-500',
        dot: 'bg-amber-500',
      },
      {
        key: 'serious',
        label: `Below ${SERIOUS_DEFAULTER_THRESHOLD}%`,
        value: inBand((percent) => percent < SERIOUS_DEFAULTER_THRESHOLD),
        stroke: 'stroke-rose-500',
        dot: 'bg-rose-500',
      },
      // Only when somebody has no mark at all, so the ring still adds up to
      // the roster without a permanent empty row in the legend.
      ...(unmarked > 0
        ? [
            {
              key: 'unmarked',
              label: 'Not marked yet',
              value: unmarked,
              stroke: 'stroke-slate-400',
              dot: 'bg-slate-400',
            },
          ]
        : []),
    ];
  }, [students]);

  /**
   * Saves what is on screen, not what is on the server.
   *
   * `rows` is already narrowed by the report type and the search box, so the
   * file matches the table the teacher is looking at. The whole class is the
   * "Class Summary" report, which is one click away.
   */
  
  useEffect(() => {
    onRegisterExport?.(exportReport, Boolean(data && rows.length > 0));
    return () => {
      onRegisterExport?.(null, false);
    };
  }, [data, rows, onRegisterExport]);
  function exportReport() {
    if (!data) return;

    downloadCsv(
      `attendance-${data.section.displayName.replace(/\s+/g, '-')}-${data.from}-to-${data.to}.csv`,
      [
        ['Roll No', 'Admission No', 'Student Name', 'Present', 'Absent', 'Late', 'Leave', 'Days Marked', 'Attendance %'],
        ...rows.map((student) => [
          student.rollNumber,
          student.admissionNumber,
          student.fullName,
          student.present,
          student.absent,
          student.late,
          student.leave,
          student.marked,
          formatPercent(student.percent),
        ]),
      ],
    );
  }

  const columns: Column<ReportRow>[] = [
    {
      key: 'serial',
      header: '#',
      align: 'center',
      className: 'w-12',
      headerClassName: 'w-12',
      render: (row) => <span className="text-xs font-semibold text-ink-3">{row.serial}</span>,
    },
    {
      key: 'name',
      header: 'Student Name',
      render: (row) => {
        const [first = '', ...rest] = row.fullName.split(' ').filter(Boolean);

        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border text-xs font-bold shadow-2xs ${getAvatarColor(row.uuid)}`}
              aria-hidden
            >
              {getInitials(first, rest.at(-1) ?? first)}
            </span>
            <span className="truncate text-sm font-bold text-ink" title={row.fullName}>
              {row.fullName}
            </span>
          </div>
        );
      },
    },
    {
      key: 'roll',
      header: 'Roll No.',
      align: 'center',
      render: (row) => (
        <span className="font-mono text-sm font-medium text-ink-2">{row.rollNumber ?? '—'}</span>
      ),
    },
    {
      key: 'present',
      header: <TwoLineHeading title="Present" unit="(Days)" />,
      align: 'center',
      render: (row) => (
        <span className="font-black tabular-nums text-emerald-600 dark:text-emerald-400">
          {row.present}
        </span>
      ),
    },
    {
      key: 'absent',
      header: <TwoLineHeading title="Absent" unit="(Days)" />,
      align: 'center',
      render: (row) => (
        <span className="font-black tabular-nums text-rose-600 dark:text-rose-400">{row.absent}</span>
      ),
    },
    {
      key: 'late',
      header: <TwoLineHeading title="Late" unit="(Days)" />,
      align: 'center',
      hideOnMobile: true,
      render: (row) => (
        <span className="font-black tabular-nums text-amber-600 dark:text-amber-400">{row.late}</span>
      ),
    },
    {
      key: 'percent',
      header: 'Attendance %',
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="min-w-[52px] text-sm font-bold tabular-nums text-ink">
            {formatPercent(row.percent)}
          </span>
          <span className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-surface-3">
            <span
              className={`block h-full rounded-full ${rateTone(row.percent)}`}
              style={{ width: `${Math.min(row.percent ?? 0, 100)}%` }}
            />
          </span>
        </div>
      ),
    },
    {
      key: 'trend',
      header: 'Trend',
      align: 'center',
      hideOnMobile: true,
      render: (row) => (
        <Sparkline
          points={row.trend}
          label={`Attendance trend for ${row.fullName}, now ${formatPercent(row.percent)}`}
          stroke={
            row.percent !== null && row.percent < DEFAULTER_THRESHOLD
              ? 'stroke-rose-500'
              : 'stroke-emerald-500'
          }
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      

      {isLoading && (
        <div className="card rounded-[16px] border border-line bg-surface p-5">
          <SkeletonLines count={8} />
        </div>
      )}

      {!isLoading && error && (
        <div className="card flex flex-col items-start gap-3 rounded-[16px] border border-line bg-surface p-5">
          <Alert tone="critical" title="Could not load this report">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      )}

      {/* Nothing was ever marked in the window. Said plainly, rather than drawn
          as a class that scored nought. */}
      {!isLoading && !error && data && data.daysRecorded === 0 && (
        <div className="card rounded-[16px] border border-line bg-surface p-8">
          <EmptyState
            icon={<CalendarDays className="h-5 w-5" />}
            title="No registers were taken in this period"
            description={`Nobody marked ${data.section.displayName} between ${formatInstituteDate(data.from)} and ${formatInstituteDate(data.to)}. Choose a wider period, or take the register on the Attendance tab.`}
          />
        </div>
      )}

      {!isLoading && !error && data && data.daysRecorded > 0 && totals && (
        <>
          {/* 2. HEADLINE FIGURES ------------------------------------------- */}
          {/* Three figures, not six. Present, absent and late totals counted
              marks — one per child per day — which no teacher could read beside
              a count of children; who was present, absent or late is the donut
              and the table below, both counted in children. */}
          {/* cursor-default: the figures are read, not clicked or selected, so
              the text cursor that appeared over them only suggested otherwise. */}
          <div className="grid cursor-default grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3.5">
            <MetricTile
              tone="blue"
              icon={<Users className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Total Students"
              value={students.length}
              hint="In this class"
            />
            <MetricTile
              tone="emerald"
              icon={<CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Average Attendance "
              value={formatPercent(totals.percent)}
              hint="For the whole class"
            />
            {/* Working days and recorded days are different facts, so both are
                shown — a rate over four of fourteen days is not a month's rate. */}
            <MetricTile
              tone="sky"
              icon={<CalendarDays className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Total Working Days"
              value={data.workingDays}
              hint={`Register taken on ${data.daysRecorded} ${data.daysRecorded === 1 ? 'day' : 'days'}`}
            />
          </div>

          {/* 3. CHARTS ------------------------------------------------------ */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="card flex flex-col justify-between rounded-[16px] border border-line bg-surface p-5 shadow-2xs lg:col-span-7">
              <div>
                <h3 className="text-base font-bold tracking-normal text-ink">Attendance Overview</h3>
                <p className="mt-0.5 text-xs font-medium text-ink-3">
                  Daily attendance trend for the selected period
                </p>
              </div>

              <AttendanceTrendChart points={data.trend} from={data.from} to={data.to} />
            </div>

            <div className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs lg:col-span-5">
              <div>
                <h3 className="text-base font-bold tracking-normal text-ink">
                  Students by attendance
                </h3>
                <p className="mt-0.5 text-xs font-medium text-ink-3">
                  Each student counted once, by their attendance in the selected period
                </p>
              </div>

              <DonutChart
                className="my-auto pt-4"
                layout="horizontal"
                slices={slices}
                total={students.length}
                caption="Students"
              />
            </div>
          </div>

          {/* 4. PER-STUDENT TABLE ------------------------------------------- */}
          <div className="card overflow-hidden rounded-[16px] border border-line bg-surface shadow-2xs">
            <div className="flex flex-col justify-between gap-3.5 border-b border-line bg-surface-2 p-4 sm:flex-row sm:items-center">
              <div>
                <h3 className="text-base font-bold tracking-normal text-ink">
                  Student Attendance Summary
                </h3>
                <p className="mt-0.5 text-sm font-medium text-ink-3">
                  Detailed attendance percentage of each student &middot;{' '}
                  {formatInstituteDate(data.from)} — {formatInstituteDate(data.to)}
                </p>
              </div>

              {/* The report type narrows this table only — the tiles and charts
                  above always count the whole class — so it sits here, beside
                  the rows it changes. */}
              <div className="flex w-full flex-col gap-2.5 sm:w-auto sm:flex-row sm:items-center">
                <div className="sm:w-56">
                  <Select
                    label="Report type"
                    hideLabel
                    options={REPORT_TYPE_OPTIONS}
                    value={reportType}
                    onChange={(event) => setReportType(event.target.value as AttendanceReportType)}
                    className="h-9 text-[13px] font-semibold"
                  />
                </div>
                <div className="sm:w-56">
                  <Input
                    label="Search students"
                    hideLabel
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search students..."
                    leftIcon={<Search className="h-3.5 w-3.5" />}
                    className="h-9 w-full text-[13px]"
                  />
                </div>

                <Button
                  variant="secondary"
                  leftIcon={<Download className="h-3.5 w-3.5" />}
                  className="h-9 shrink-0 text-[13px] font-semibold"
                  onClick={exportReport}
                  disabled={rows.length === 0}
                >
                  Export
                </Button>
              </div>
            </div>

            <Table
              variant="plain"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.uuid}
              empty={
                <EmptyState
                  icon={<FileBarChart className="h-5 w-5" />}
                  title={searchQuery ? 'No students match your search' : 'No students in this report'}
                  description={
                    searchQuery
                      ? 'Check the spelling, or clear the search to see the whole class.'
                      : 'Nobody in this class falls into the report type you chose. Try “Class Summary” to see everyone.'
                  }
                />
              }
            />
          </div>
        </>
      )}
    </div>
  );
}

/** A heading with its unit under it, so "Present" and "(Days)" do not compete. */
function TwoLineHeading({ title, unit }: { title: string; unit: string }) {
  return (
    <span className="block leading-tight">
      {title}
      <span className="block text-2xs font-semibold text-ink-3">{unit}</span>
    </span>
  );
}

interface TrendPoint {
  date: string;
  percent: number | null;
}

/** Whole days since the epoch, for placing a `YYYY-MM-DD` along an axis. */
function dayNumber(date: string): number {
  return Math.round(Date.parse(`${date}T00:00:00.000Z`) / 86_400_000);
}

/** `YYYY-MM-DD` for a day number — the inverse of `dayNumber`. */
function dayString(day: number): string {
  return new Date(day * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The daily rate across the period.
 *
 * The x-axis is the period the header asked for, not the list of recorded days:
 * spaced by recorded day, any window holding the same few registers drew the
 * same line under the same dates, and changing the dates looked like it did
 * nothing. Placed by date, a day nobody marked is a visible gap, and the axis
 * always reads from the chosen start to the chosen end.
 */
function AttendanceTrendChart({ points, from, to }: { points: TrendPoint[]; from: string; to: string }) {
  const usable = points.filter((point): point is TrendPoint & { percent: number } =>
    point.percent !== null,
  );

  if (usable.length === 0) {
    return (
      <p className="mt-4 flex h-56 items-center justify-center text-center text-xs font-medium text-ink-3">
        No attendance was recorded between {formatInstituteDate(from)} and {formatInstituteDate(to)}.
      </p>
    );
  }

  const start = dayNumber(from);
  const span = Math.max(1, dayNumber(to) - start);
  const xOf = (date: string) => 50 + ((dayNumber(date) - start) / span) * 430;

  // Same geometry the card was drawn to: 100% sits at y=25, 0% at y=160.
  const plotted = usable.map((point) => ({
    ...point,
    x: xOf(point.date),
    y: 160 - (point.percent / 100) * 135,
  }));

  const linePath = plotted.reduce((path, point, index, all) => {
    if (index === 0) return `M ${point.x},${point.y}`;
    const previous = all[index - 1];
    const midX = (previous.x + point.x) / 2;

    return `${path} C ${midX},${previous.y} ${midX},${point.y} ${point.x},${point.y}`;
  }, '');

  // A single day is a dot, not an area.
  const areaPath =
    plotted.length > 1
      ? `${linePath} L ${plotted[plotted.length - 1].x},160 L ${plotted[0].x},160 Z`
      : '';

  // At most seven dates along the period, ends included, so a term does not
  // print ninety dates on top of one another.
  const steps = Math.min(6, span);
  const labels = Array.from({ length: steps + 1 }, (_, index) =>
    dayString(start + Math.round((index / steps) * span)),
  );

  return (
    <div className="relative mt-4 flex h-56 w-full flex-col justify-end">
      <svg
        viewBox="0 0 500 180"
        className="h-full w-full overflow-visible"
        preserveAspectRatio="none"
        role="img"
        aria-label={`Daily attendance from ${usable[0].date} to ${usable[usable.length - 1].date}`}
      >
        <defs>
          <linearGradient id="attendance-line-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[25, 58, 92, 126].map((y) => (
          <line
            key={y}
            x1="40"
            y1={y}
            x2="490"
            y2={y}
            stroke="currentColor"
            strokeDasharray="3 3"
            className="stroke-current text-line opacity-60"
          />
        ))}
        <line x1="40" y1="160" x2="490" y2="160" stroke="currentColor" className="stroke-current text-line" />

        {[
          { y: 29, label: '100%' },
          { y: 62, label: '75%' },
          { y: 96, label: '50%' },
          { y: 130, label: '25%' },
          { y: 164, label: '0%' },
        ].map((tick) => (
          <text
            key={tick.label}
            x="32"
            y={tick.y}
            textAnchor="end"
            className="fill-current text-[10px] font-semibold text-ink-3"
          >
            {tick.label}
          </text>
        ))}

        {/* The fill and the line are the same colour, so the gradient is taken
            from `currentColor` rather than repeating a literal in two places. */}
        <g className="text-emerald-500">
          {areaPath && <path d={areaPath} fill="url(#attendance-line-gradient)" />}
          <path
            d={linePath}
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {plotted.map((point) => (
            <circle
              key={point.date}
              cx={point.x}
              cy={point.y}
              r="4"
              className="fill-surface stroke-current stroke-2"
            >
              <title>{`${formatInstituteDate(point.date)}: ${point.percent}% present`}</title>
            </circle>
          ))}
        </g>
      </svg>

      {/* Each date sits under its own point on the axis: the svg stretches to
          the card's width, so a position in its 500-wide box is a share of it. */}
      <div className="relative mt-2 h-4 text-[10px] font-semibold text-ink-3" aria-hidden>
        {labels.map((date) => (
          <span
            key={date}
            className="absolute -translate-x-1/2 whitespace-nowrap"
            style={{ left: `${(xOf(date) / 500) * 100}%` }}
          >
            {new Date(`${date}T00:00:00.000Z`).toLocaleDateString('en-IN', {
              timeZone: 'UTC',
              day: '2-digit',
              month: 'short',
            })}
          </span>
        ))}
      </div>
    </div>
  );
}
